import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import API_ENDPOINTS from '../config/api';
import AuroraBackground from './AuroraBackground';
import './LandingPage.css';

const CONTEXT_FEATURES = [
  'Scan your org into a living context graph — objects, fields, flows, automations',
  'AI-written descriptions for every undocumented field and flow',
  'Interactive flow diagrams and a browsable data dictionary',
  'Context Packs: markdown bundles your AI agents can read',
];

const RADAR_FEATURES = [
  'Every flow failure captured in-org as a record — nothing else leaves',
  'Automatic root-cause diagnosis, graded Confirmed or Probable',
  'Recurrence grouping: 112 identical failures is one problem',
  'Context-aware: diagnoses cite your fields, types, and recent changes',
];

const LandingPage = ({ onConnect }) => {
  const [loginType, setLoginType] = useState('production');

  const handleConnect = () => {
    window.location.href = `${API_ENDPOINTS.AUTH_LOGIN}?env=${loginType}`;
  };

  return (
    <>
      <AuroraBackground />
      <div className="landing-page">
        {/* Hero */}
        <div className="landing-hero">
          <h1 className="landing-title">
            <span className="force-text">Force</span>
            <span className="sight-text">Sight</span>
          </h1>
          <p className="landing-tagline">
            See your whole org. And give your agents the same sight.
          </p>
          <p className="landing-subtag">
            One platform, two instruments: a free context engine that turns your
            Salesforce metadata into navigable, agent-readable documentation —
            and a paid radar that uses that context to diagnose flow failures
            before your users notice.
          </p>
        </div>

        {/* Login card */}
        <div className="glass-card landing-login-card" id="connect">
          <div className="landing-env-selector">
            <label className={loginType === 'production' ? 'active' : ''}>
              <input
                type="radio"
                value="production"
                checked={loginType === 'production'}
                onChange={(e) => setLoginType(e.target.value)}
              />
              Production / Developer
            </label>
            <label className={loginType === 'sandbox' ? 'active' : ''}>
              <input
                type="radio"
                value="sandbox"
                checked={loginType === 'sandbox'}
                onChange={(e) => setLoginType(e.target.value)}
              />
              Sandbox
            </label>
          </div>

          <button
            onClick={handleConnect}
            className="landing-connect-btn"
            title="Redirect to Salesforce to authorize ForceSight"
          >
            Login with Salesforce
          </button>

          <p className="landing-security-note">
            You'll be redirected to Salesforce to authorize. Metadata only — we
            never read or store record data.
          </p>
        </div>

        {/* Two products, one pipeline */}
        <div className="landing-products">
          <div className="glass-card product-card product-context">
            <span className="product-badge badge-free">Free · available now</span>
            <h2>Context Engine</h2>
            <p className="product-promise">
              Your org, understood — by your team and your AI.
            </p>
            <ul>
              {CONTEXT_FEATURES.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <a className="product-cta cta-solid" href="#connect">
              Start free — connect your org
            </a>
          </div>

          <div className="product-pipeline" aria-hidden="true">
            <span className="pipe-node">scan</span>
            <span className="pipe-line" />
            <span className="pipe-node pipe-node-core">context&nbsp;graph</span>
            <span className="pipe-line" />
            <span className="pipe-node">diagnosis</span>
          </div>

          <div className="glass-card product-card product-radar">
            <span className="product-badge badge-paid">
              Paid · coming to AppExchange
            </span>
            <h2>Flow Radar</h2>
            <p className="product-promise">
              What broke, why it broke, and how to fix it.
            </p>
            <ul>
              {RADAR_FEATURES.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <Link className="product-cta cta-ghost" to="/flow-radar">
              Learn about Flow Radar &rarr;
            </Link>
          </div>
        </div>

        {/* Better together */}
        <div className="landing-together">
          <p>
            <strong>Better together.</strong> The Context Engine gives you
            sight; Flow Radar uses that sight. Connected, a failure isn&rsquo;t
            &ldquo;a field was too small&rdquo; — it&rsquo;s{' '}
            <code>Case.Summary__c</code> is <code>Text(255)</code> and{' '}
            <code>Batch_Notes__c</code> was retyped in last week&rsquo;s
            deployment.
          </p>
        </div>

        {/* Footer */}
        <div className="landing-footer">
          <p className="landing-footer-secure">
            Secure OAuth authentication powered by Salesforce &middot;{' '}
            <Link to="/pricing">Pricing</Link>
          </p>
          <p className="landing-footer-copy">
            &copy; 2026 ForceSight by Starfish Group
          </p>
        </div>
      </div>
    </>
  );
};

export default LandingPage;
